/**
 * @module experience/landing
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/* tslint:disable:max-file-line-count */

// Core Libraries
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  EventEmitter,
  Input,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Wove } from 'aspect.js/';
import { takeUntil } from 'rxjs/operators';
// Pipes
import { PhonePipe } from '@support/shared/widgets/pipes/phone/phone.pipe';

// Services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { UserService } from '@support/shared/services/user.service';
import { AccountLandingService } from '@experience/landing/common/components/account-landing/services/account-landing.service';
import { NavbarService } from '@root/shared/services/navbar.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';

// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { AlertEventInterface } from '@support/shared/widgets/interfaces/toggle-alert.interface';
import { CustomerDetailsResponse } from '@support/api/proxies/responses/api-2007.interface';

import {
  CustomerAccountBillingDetails,
  LineSummary,
} from '@support/api/proxies/responses/api-2006.interface';
import {
  ACCOUNT_LANDING_MODULE_COMPONENTS,
  ACCOUNT_LANDING_CONSTANTS,
} from '@experience/landing/common/constants/account-landing.constants';
import { AccountLineDetailsModel } from '@experience/landing/common/components/account-landing/models/account-line-details.model';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { SETTINGS } from '@environment/settings';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { RepdashWindowRef } from '@experience/repdash/interfaces/repdash.interface';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { CustomerProfileService } from '@support/customer-login/services/customer-profile.service';
import { StateStoreService } from '@support/state-object-hash-utilities/services/state-store.service';
import { CacheManagerService } from '@support/shared/services/cache-manager.service';
import { ClearCacheResponse } from '@support/shared/interfaces/clear-cache.interface';
import { APIError } from '@support/api/common/error.interface';
import { HttpResponse } from '@angular/common/http';
import { CPS_ROUTES, EBB_ROUTES } from '@support/shared/constants/app-routes.constants';
import { UsageBucketModel } from '@experience/landing/common/models/usage-bucket.model';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { CustomerAccountLineBillingDetails } from '@support/api/proxies/responses/api-2040.interface';
import { UIPermissionService } from '@support/shared/widgets/services/ui-permission.service';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { ROUTER_CONSTANTS } from '../../constants/line-details.constants';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { FAN } from '@support/api/proxies/responses/api-2038.interface';

@Wove({ moduleName: ACCOUNT_LANDING_MODULE_COMPONENTS.ACCOUNT_LANDING_MODULE_NAME })
/**
 * Contains account information on the account landing page
 */
@Component({
  selector: 'tmo-account-landing',
  templateUrl: './account-landing.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/html-spinner.component.scss',
  ],
})
export class AccountLandingComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * This will work as the holder for the child component target
   */
  @ViewChild('target', { static: false }) public target: ElementRef;
  /**
   * stores usage model
   */
  @Input() public usageBucketModel: UsageBucketModel;
  /**
   * Toggles the Alerts
   */
  public toggleAlerts: boolean;
  /**
   * AEM data for account landing page
   */
  public contentData: ConfigData;
  /**
   * To show nickName of customer
   */
  public nickName: string;

  /**
   * Stores string value for Renewal Date
   */
  public renewalDateText: string;
  /**
   * Stores bill block details
   */
  public customerAccountBillingDetails: CustomerAccountBillingDetails;
  /**
   * Stores credit level of the customer
   */
  public creditLevel: number;
  /**
   * Stores flag for billingTypePrepaid
   */
  public isBillingTypePrepaid: boolean;
  /**
   * Store value to open accordion
   */
  public openAccordion: boolean;
  /**
   * Store the total number of notifications
   */
  public totalNotifications: number;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * Flag to wait until response is available
   */
  public waitForResponse: boolean;

  public accountLineDetailsModel: AccountLineDetailsModel;
  /**
   * Flag to check if channel is web
   */
  // public isWebChannel: boolean;
  /**
   * Flag to check if refresh cta is clicked
   */
  public isRefresh: boolean;
  /**
   * Flag to show/hide spinner
   */
  public isShowSpinner: boolean;
  /**
   * Flag to show Fraud lock
   */
  public isFraudLock: boolean;
  /**
   * Stores whether tourist plan is present in cart
   */
  public isTouristPlan: boolean;
  /**
   * Stores whether tourist plan is present in cart
   */
  public permissionEnabled: boolean;
  /**
   * Stores fraud modal
   */
  public fraudModal: StaticModal;
  /**
   * Stores primary msisdn for an account
   */
  public primaryMsisdn: string;
  /**
   * variable to store fraud lock message
   */
  public fraudLockIndicator: string;
  /**
   * Stores repdashWindowReference
   */
  public repDashwindow: RepdashWindowRef;
  /**
   * Boolean to check if modal has been closed or not
   */
  public isModalClosed: boolean;

  public isP10MCustomer: boolean;

  public msisdnInput: string;

  /**
   * Dynamic Modal for welcome message post Migration
   */
  public migrationWelcomeModal: DynamicModal;

  /**
   * Is add a line displayed / enabled
   */
  public showAddALine: boolean;

  constructor(
    private userService: UserService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private accountLandingService: AccountLandingService,
    private commonUtil: CommonUtilService,
    private navBarService: NavbarService,
    private objectService: ObjectService,
    private route: ActivatedRoute,
    private phonePipe: PhonePipe,
    public routeSnapshot: ActivatedRoute,
    private router: Router,
    private windowRefService: WindowRefService,
    private customerProfileService: CustomerProfileService,
    private stateStoreService: StateStoreService,
    private permissionService: UIPermissionService,
    private cacheManagerService: CacheManagerService,
    public pdlService: PdlService,
    public pdlWrapperService: PdlWrapperService,
    private cartSupportService: CartSupportService,
    private pdpDataHandlerService: PdpDataHandlerService
  ) {
    super(pdlService, route);
    this.toggleAlerts = true;
    this.totalNotifications = 0;
    this.openAccordion = false;
    this.isBillingTypePrepaid = false;
    this.waitForResponse = true;
    this.ngUnsubscribe = new Subject<void>();
    // this.isWebChannel = false;
    this.isTouristPlan = false;
    this.isRefresh = false;
    this.isShowSpinner = false;
    this.fraudModal = this.accountLandingService.createFraudModal();
    this.migrationWelcomeModal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'rpx-miration-modal',
    };
  }

  /**
   * Loads the initial data of Account Landing Page
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.isShowSpinner = true;
    this.repDashwindow = this.windowRefService.nativeWindow();
    // this.isWebChannel =  SETTINGS.CHANNEL.toLowerCase() === ACCOUNT_LANDING_CONSTANTS.isWeb;
    this.contentData = null;
    this.nickName = '';
    this.primaryMsisdn = '';
    if (this.stateStoreUtilsService.getFreshDataAvailableFlag()) {
      this.cacheManagerService.enableRefreshButton();
    }
    this.cacheManagerService.refreshPage$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        this.cacheManagerService
          .clearCacheForInteractionIds()
          .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
          .subscribe((response: ClearCacheResponse) => {
            if (response.Result === 'Success') {
              this.isRefresh = true;
              this.isShowSpinner = true;
              this.stateStoreUtilsService.clearBillingBlockDetails();
              this.stateStoreUtilsService.clearAccountLinesInfo();
              this.stateStoreUtilsService.clearCustomerAccountLineBillingDetail();
              this.loadAccountLandingData();
            }
          });
      });
    if (!this.userService.isUserLookedUp) {
      this.fetchAccountLandingData();
    }
    this.userService.loadSpinnerSubject$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: boolean) => {
        this.waitForResponse = data;
      });
    this.userService.p10CustomerLoggedIn$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        this.isP10MCustomer = data;
      });
    this.userService.infoChangeSubject
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: { loadAccountLanding: boolean }) => {
        if (event.loadAccountLanding) {
          this.fetchAccountLandingData();
        }
      });
    this.route.queryParamMap.subscribe((params: Params) => {
      if (
        params.get(ACCOUNT_LANDING_CONSTANTS.SECONDARY_LINK) === ACCOUNT_LANDING_CONSTANTS.FALSE
      ) {
        this.navBarService.navbarChangeEmitter.emit({ id: ACCOUNT_LANDING_CONSTANTS.DEFAULT });
      }
    });
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Event emitter triggered on changes of model on page header
   * @param event : toggle alerts for AlertEventInterface
   * @returns void
   */
  public toggleAlertChange(event: AlertEventInterface): void {
    if (this.toggleAlerts !== event.toggleAlerts) {
      this.toggleAlerts = event.toggleAlerts;
    }
  }
  /**
   * Function to get the total notifications
   * @param  $event: number
   * @returns void
   */
  public updateNotifications($event: number): void {
    this.totalNotifications = $event;
  }

  /**
   * Fetches the account landing data
   * @returns void
   */
  public fetchAccountLandingData(): void {
    this.resetAccountLandingData();
    this.loadAccountLandingData();
  }

  /**
   * Loads the customer Data
   * @returns void
   */
  private loadAccountLandingData(): void {
    this.loadAEMData();
    this.loadBillBlockDetail();
  }

  /**
   * Loads the bill block details
   * @returns void
   */
  public loadBillBlockDetail(): void {
    this.userService.isUserLookedUp = false;
    this.userService
      .getCustomerAccountBillingSummary()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: CustomerAccountBillingDetails) => {
          window.dispatchEvent(new CustomEvent(ACCOUNT_LANDING_CONSTANTS.LOAD_ACCOUNT_LANDING, {}));
          if (this.commonUtil.isNotEmpty(response)) {
            const lineId = this.objectService.get(response, 'lines.0.lineOfServiceId');
            // if (this.isWebChannel) {
            this.stateStoreUtilsService.setBillingTypeOfCustomerInFocus(response.billingType);
            const showModal = this.objectService.get(response, 'u2ActivationIndicator', false);
            if (showModal) {
              this.handleRPXAccountSetup(lineId);
            }
            // }
            this.customerAccountBillingDetails = response;
            this.accountLineDetailsModel = new AccountLineDetailsModel(
              this.objectService.get(response, 'lines', []),
              this.commonUtil,
              this.stateStoreUtilsService
            );
            this.stateStoreUtilsService.setLineId(lineId);
            this.isTouristPlan = this.objectService.get(response, 'touristPlanIndicator');
            this.navBarService.touristPlanEmitter.emit(this.isTouristPlan);
            this.primaryMsisdn = this.getPrimaryMsisdn(response.lines);
            this.isFraudLock = this.objectService.get(response, 'fraudLockIndicator');
            const customerId = this.stateStoreUtilsService.getCustomerId();
            const accountId = this.stateStoreUtilsService.getAccountId();
            if (customerId && accountId) {
              this.getPermissions();
            } else {
              this.openFraudModal(this.contentData);
            }
            this.getExistingLineDetails();
          }
          this.getCustomerDetails();
        },
        (error: APIError[]) => {
          window.dispatchEvent(new CustomEvent(ACCOUNT_LANDING_CONSTANTS.LOAD_ACCOUNT_LANDING, {}));
          this.isShowSpinner = false;
        }
      );
  }
  /**
   * Function used to get existing line details
   */
  private getExistingLineDetails(): void {
    if (this.stateStoreUtilsService.getCustomerId()) {
      this.cartSupportService
        .getExistingLineDetails()
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (cart: FAN) => {
            this.showAddALine =
              this.cartSupportService.checkForAddALinePermission(
                PERMISSION_CONSTANTS.BROWSE_PHONES
              ) && !this.cartSupportService.isMaxLineReached();
          },
          (error: APIError) => {}
        );
    }
  }

  /**
   * handleModalButtonClickEvent
   * @param event Event
   * @returns void
   */
  public handleModalButtonClickEvent(event: Event): void {
    if (!this.isModalClosed) {
      // if (!this.isWebChannel) {
      //   this.repDashwindow.RepdashEvent
      //     .dispatchEvent(new CustomEvent('fraudRemoveAccount'));
      // }else {
      this.goToEui();
      // }
    }
    this.isModalClosed = true;
  }
  /**
   * Method to open fraudModal in case of fraudlent activity on account
   * @param contentData ConfigData
   * @returns void
   */
  private openFraudModal(contentData: ConfigData): void {
    const fraudStatus = this.stateStoreUtilsService.getAccountFraudStatus();
    if (fraudStatus) {
      this.fraudModal.headerText = contentData.underReview;
      this.fraudModal.buttons[0].name = contentData.close;
      // if (SETTINGS.CHANNEL.toLowerCase() === 'retail') {
      //   this.fraudModal.bodyText = contentData.contactRSL;
      // }else if (this.isWebChannel) {
      this.fraudModal.bodyText = contentData.contactCustomerCare;
      // }else{
      //   this.fraudModal.bodyText = contentData.contactSAPCare;
      // }
      this.fraudModal.toggleModalEmitter.emit({ open: true });
    }
  }
  /**
   * Method to redirect to EUI login page
   */
  public goToEui(): void {
    this.customerProfileService
      .invalidateToken()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((response: Response) => {
        this.stateStoreService.clearState();
      });
    const win = this.windowRefService.nativeWindow();
    win.open(
      `${SETTINGS.EUIROOT}auth?redirect_uri=${win.location.protocol}//${win.location.host}`,
      '_self'
    );
  }

  /**
   * method to get permissions if fraud indicator
   */
  private getPermissions(): void {
    this.customerProfileService
      .getPermissions(
        this.stateStoreUtilsService.getCustomerId(),
        this.stateStoreUtilsService.getAccountId()
      )
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((permissions: HttpResponse<Object>) => {
        this.stateStoreUtilsService.setPermission(permissions.body);
        this.customerProfileService.permissionsLoadedSource.next(true);
        this.navBarService.permissionsUpdated.emit(true);
        this.permissionEnabled = this.permissionService.checkMultiplePermissions(
          ROUTER_CONSTANTS.LINE_LANDING,
          PERMISSION_CONSTANTS.CHANGE_RTPLN_LOST_STOLEN,
          PERMISSION_CONSTANTS.CHANGE_RATE_PLAN,
          'RepOnly',
          'common'
        );
      });
  }

  /**
   * Loads the AEM Data
   * @returns void
   */
  private loadAEMData(): void {
    this.accountLandingService
      .getContentData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((contentData: ConfigData) => {
        this.contentData = contentData;
        // this.fraudLockIndicator = this.isWebChannel ? this.objectService.get(this.contentData, 'fraudLockIndicatorMessage')
        // : this.objectService.get(this.contentData, 'fraudLockMessage');
        this.fraudLockIndicator = this.objectService.get(
          this.contentData,
          'fraudLockIndicatorMessage'
        );
        this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
      });
  }

  /**
   * Reset Account Landing Data
   * @returns void
   */
  private resetAccountLandingData(): void {
    this.contentData = null;
    this.customerAccountBillingDetails = null;
  }

  /**
   * Gets nick name from API 2007
   * @returns void
   */
  private getCustomerDetails(): void {
    this.userService
      .getCustomerDetailSearch()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (customerDetails: CustomerDetailsResponse) => {
          if (customerDetails && customerDetails.customer) {
            this.nickName =
              customerDetails.customer.firstName &&
              customerDetails.customer.firstName !== '' &&
              customerDetails.customer.lastName &&
              customerDetails.customer.lastName !== ''
                ? customerDetails.customer.firstName + ' ' + customerDetails.customer.lastName
                : this.primaryMsisdn;
          }
          if (this.stateStoreUtilsService.getFreshDataAvailableFlag() && this.isRefresh) {
            this.cacheManagerService.enableRefreshButton();
            this.cacheManagerService.notifyRepdashToUpdateInfoBar();
          }
          const userData = this.pdpDataHandlerService.setUserData();
          this.pdlWrapperService.trackData('Product-Detail', 'product', { user: userData });
          this.isShowSpinner = false;
        },
        (error: APIError[]) => {
          this.isShowSpinner = false;
        }
      );
  }

  /**
   * Gets primary msisdn from API 2007
   * @param lines LineSummary[]
   * @returns string
   */
  private getPrimaryMsisdn(lines: LineSummary[]): string {
    let primaryMsisdn: string = '';
    if (lines) {
      const line: LineSummary = lines.find((lineDetail: LineSummary) => {
        return (
          this.objectService.get(lineDetail, 'primaryLineIndicator', '').toLocaleLowerCase() ===
          ACCOUNT_LANDING_CONSTANTS.PRIMARY
        );
      });
      primaryMsisdn = line ? line.msisdn : lines[0].msisdn;
    }
    this.msisdnInput = primaryMsisdn;
    if (this.msisdnInput) {
      this.stateStoreUtilsService.setAuthPrimaryMsisdn(this.msisdnInput);
    }
    return this.phonePipe.transform(primaryMsisdn) || '';
  }
  /**
   * Checks if auto navigate is needed and then navigates if parameter passed is true
   * @returns void
   */
  public checkAutoNavigate(isAutoNavigate: boolean): void {
    const custAccountLineDetails = this.accountLineDetailsModel.customerAccountLineDetails;
    const plansFromContent = this.contentData.plansForRedirect
      ? this.contentData.plansForRedirect.split('|')
      : '';
    let isPlanAvailable = false;
    for (const custAccountLineDetail of custAccountLineDetails) {
      isPlanAvailable = plansFromContent.indexOf(custAccountLineDetail.planOfferId) > -1;
      if (isPlanAvailable) {
        break;
      }
    }
    // if (isAutoNavigate && this.accountLandingService.checkNavigation && isPlanAvailable && this.isWebChannel) {
    if (isAutoNavigate && this.accountLandingService.checkNavigation && isPlanAvailable) {
      this.accountLandingService.checkNavigation = false;
      this.router.navigate([CPS_ROUTES.SESSION_PASS]);
    }
  }

  /**
   * Handling for Migrated Account Modal on WEB.
   */
  private handleRPXAccountSetup(lineId: string = ''): void {
    this.userService
      .getCustomerBillingDetailsData(lineId)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((lineDetails: CustomerAccountLineBillingDetails) => {
        const migrationIndicator = this.objectService.get(
          lineDetails,
          'lineDetails.migrationIndicator'
        );
        const modalAlreadyShown = this.stateStoreUtilsService.getRPXMigrationModalStatus();
        if (migrationIndicator && !modalAlreadyShown) {
          this.stateStoreUtilsService.setRPXMigrationModalStatus(true);
          this.migrationWelcomeModal.toggleModalEmitter.emit({ open: true, close: false });
        }
      });
  }

  public closeMigrationModal(): void {
    this.migrationWelcomeModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
  }

  /**
   * Method to navigate to ebb eligibility page
   * @returns void
   */
  public navigateToEbb(): void {
    this.router.navigate([EBB_ROUTES.EBB_ELIGIBILITY]);
  }
}
